import {FormField, OptionalFormField, RequiredFormField} from "../../../../../validation/FormField";
import {none, Option, option} from "ts-option";
import FormValidation from "../../../../../validation/FormValidation";
import {ValidationResult} from "../../../../../validation/ValidationResult";
import FormValidators from "../../../../../validation/FormValidators";
import BaseFormController from "../../../../../validation/BaseFormController";
import {BillOfMaterialsPreviewAsset} from "../../../../../model/job/BillOfMaterialsPreviewItems";
import Price, {CurrencyIsoCode} from "../../../../../model/common/Price";
import {BomItem} from "../../../../../model/job/BomItems";

const uuidv4 = require("uuid/v4");

/**
 * Asset form data.
 */
export default class BillOfMaterialsAssetForm extends BaseFormController<BillOfMaterialsAssetForm> {
	constructor(formId: string,
		public readonly manufacturerName: OptionalFormField<string>,
		public readonly name: RequiredFormField<string>,
		public readonly sellUnitPrice: RequiredFormField<string>,
		public readonly markup: RequiredFormField<number>,
		public readonly quantity: RequiredFormField<number>,
		public readonly rebateUnitPrice: RequiredFormField<string>,
		public readonly selfSourced: RequiredFormField<boolean>) {
		super(name, formId);
	}

	static createBillOfMaterialsAssetForm({
		manufacturerName = none,
		name = none,
		sellUnitPrice = none,
		markup = none,
		quantity = none,
		rebateUnitPrice = none,
		selfSourced = none
	}) {
		return new BillOfMaterialsAssetForm(
			uuidv4(),
			OptionalFormField.create<string>(manufacturerName),
			RequiredFormField.create<string>(name, "Model is required", [
				FormValidators.nonEmptyString("Model is required")
			]),
			RequiredFormField.create<string>(sellUnitPrice, "Price is required"),
			RequiredFormField.create<number>(markup, "Markup is required"),
			RequiredFormField.create<number>(quantity, "Quantity is required"),
			RequiredFormField.create<string>(rebateUnitPrice, "Rebate is required"),
			RequiredFormField.create<boolean>(selfSourced, "Self sourced is required"),
		)
	}

	public static createEmpty(): BillOfMaterialsAssetForm {
		return this.createBillOfMaterialsAssetForm({});
	}

	static createFromBillOfMaterialsAsset(bomAsset: BomItem) {
		return BillOfMaterialsAssetForm.createBillOfMaterialsAssetForm({
			manufacturerName: bomAsset.manufacturerName,
			name: option(bomAsset.name),
			sellUnitPrice: option(bomAsset.sellUnitPrice.amount.toFixed(2)),
			markup: option(bomAsset.markup),
			quantity: option(bomAsset.quantity),
			rebateUnitPrice: option(bomAsset.rebateUnitPrice.amount.toFixed(2)),
			selfSourced: option(bomAsset.selfSourced)
		});
	}

	validate(): BillOfMaterialsAssetForm {
		return FormValidation.validateForm<BillOfMaterialsAssetForm>(this, (form, config) => form.copy(config));
	}

	onValid(onValid: (bomAsset: BillOfMaterialsPreviewAsset) => void): ValidationResult<BillOfMaterialsPreviewAsset> {
		return FormValidation.validateFields(
			[this.name, this.sellUnitPrice, this.markup, this.quantity, this.rebateUnitPrice, this.selfSourced],
			() => {
				const sellUnitPrice = new Price(Number(this.sellUnitPrice.getValue()), CurrencyIsoCode.GBP);
				const rebateUnitPrice = new Price(Number(this.rebateUnitPrice.getValue()), CurrencyIsoCode.GBP)
				return new BomItem(uuidv4(),
					none,
					this.manufacturerName.getOptionalValue(),
					this.markup.getValue(),
					null,
					[],
					this.name.getValue(),
					none,
					none,
					this.quantity.getValue(),
					rebateUnitPrice,
					sellUnitPrice,
					rebateUnitPrice,
					sellUnitPrice,
					this.selfSourced.getValue()
				);
			}).onSuccess(bomAsset => onValid(bomAsset));
	}

	copy({
		manufacturerName = this.manufacturerName,
		name = this.name,
		sellUnitPrice = this.sellUnitPrice,
		markup = this.markup,
		quantity = this.quantity,
		rebateUnitPrice = this.rebateUnitPrice,
		selfSourced = this.selfSourced
	}): BillOfMaterialsAssetForm {
		return new BillOfMaterialsAssetForm(this.formId, manufacturerName, name, sellUnitPrice, markup, quantity, rebateUnitPrice,
			selfSourced);
	}

	fields(): Array<FormField<any>> {
		return [];
	}
}
